import * as React from "react";
import { useEffect } from "react";
import "./SessionManagementPanel.scss"
import { Fieldset } from "primereact/fieldset";
import { useTranslation } from "react-i18next";
import UserInterface from "@dashart/dashart-gpt-shared-library/dist/interfaces/UserInterface";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { SessionInfo, SessionService } from "@app/services/SessionService";
import { AuthUtil } from "@app/utils/AuthUtil";
import { useSetAtom } from "jotai";
import { lastToastMessageAtom } from "@app/models/AppStates";
import { ToastMessageInterface } from "@app/interfaces/ToastMessageInterface";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { DateTime } from "luxon";

export interface SessionManagementPanelProps {
    currentUser: UserInterface;
    user: UserInterface;
}

/**
 * Parse user agent string to a short browser/device description
 */
const parseUserAgent = (ua: string | null): string => {
    if (!ua) return '-';

    // Detect browser
    let browser = 'Unknown';
    if (ua.includes('Firefox/')) browser = 'Firefox';
    else if (ua.includes('Edg/')) browser = 'Edge';
    else if (ua.includes('Chrome/')) browser = 'Chrome';
    else if (ua.includes('Safari/') && !ua.includes('Chrome/')) browser = 'Safari';
    else if (ua.includes('OPR/')) browser = 'Opera';

    // Detect OS
    let os = '';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    return os ? `${browser} / ${os}` : browser;
};

const SessionManagementPanel: React.FunctionComponent<SessionManagementPanelProps> = (props: SessionManagementPanelProps) => {
    const { currentUser, user } = props;
    const { t } = useTranslation();
    const [sessions, setSessions] = React.useState<SessionInfo[]>([]);
    const setLastToastMessage = useSetAtom(lastToastMessageAtom);

    const [showRevokeDialog, setShowRevokeDialog] = React.useState(false);
    const [sessionToRevoke, setSessionToRevoke] = React.useState<SessionInfo | null>(null);

    // Only show for own profile
    const isOwnProfile = currentUser?.id === user?.id;

    useEffect(() => {
        if (isOwnProfile) {
            loadSessions();
        }
    }, [user, isOwnProfile]);

    const loadSessions = () => {
        const refreshToken = AuthUtil.getRefreshToken() ?? undefined;
        SessionService.getActiveSessions(refreshToken)
            .then((data) => {
                setSessions(data);
            }).catch((error) => {
                setLastToastMessage({
                    severity: "error",
                    summary: t("components.session_management_panel.load_error"),
                    detail: error?.message
                } as ToastMessageInterface);
            })
    }

    const revokeSession = () => {
        if (!sessionToRevoke) return;
        setShowRevokeDialog(false);

        SessionService.revokeSession(sessionToRevoke.id)
            .then(() => {
                setLastToastMessage({
                    severity: "success",
                    summary: t("components.session_management_panel.revoke_success"),
                });
                setSessionToRevoke(null);
                loadSessions();
            }).catch((error) => {
                setLastToastMessage({
                    severity: "error",
                    summary: t("components.session_management_panel.revoke_error"),
                    detail: JSON.stringify(error)
                } as ToastMessageInterface);
            });
    }

    const revokeOtherSessions = () => {
        const refreshToken = AuthUtil.getRefreshToken();
        if (!refreshToken) return;

        SessionService.revokeOtherSessions(refreshToken)
            .then(() => {
                setLastToastMessage({
                    severity: "success",
                    summary: t("components.session_management_panel.revoke_others_success"),
                });
                loadSessions();
            }).catch((error) => {
                setLastToastMessage({
                    severity: "error",
                    summary: t("components.session_management_panel.revoke_error"),
                    detail: JSON.stringify(error)
                } as ToastMessageInterface);
            });
    }

    const cellParserBrowser = (rowData: SessionInfo) => {
        const isCurrentSession = rowData.isCurrent === true;
        return (
            <div className="flex align-items-center gap-2">
                <span>{parseUserAgent(rowData.userAgent)}</span>
                {isCurrentSession && (
                    <Tag
                        value={t("components.session_management_panel.label_current")}
                        severity="success"
                    />
                )}
            </div>
        );
    };

    const cellParserActions = (rowData: SessionInfo) => {
        const isCurrentSession = rowData.isCurrent === true;
        if (isCurrentSession) return null;

        return (
            <Button
                onClick={() => {
                    setSessionToRevoke(rowData);
                    setShowRevokeDialog(true);
                }}
                icon="pi pi-sign-out"
                className="p-button-rounded p-button-danger p-button-text mr-1"
                tooltip={t("components.session_management_panel.revoke_session")}
            />
        );
    };

    const footerActions = () => {
        if (sessions.length <= 1) return null;

        return (
            <div className="flex flex-row justify-content-end w-full">
                <Button
                    className="p-button-sm p-button-outlined p-button-danger"
                    label={t('components.session_management_panel.revoke_others')}
                    icon="pi pi-fw pi-sign-out"
                    onClick={revokeOtherSessions}
                />
            </div>
        );
    };

    const revokeConfirmDialog = () => {
        if (!showRevokeDialog || !sessionToRevoke) return null;

        return (
            <Dialog
                header={t('components.session_management_panel.confirm_revoke_header')}
                visible={showRevokeDialog}
                style={{ width: '30vw' }}
                onHide={() => setShowRevokeDialog(false)}
                footer={
                    <div>
                        <Button
                            label={t('globals.actions.no')}
                            icon="pi pi-times"
                            onClick={() => setShowRevokeDialog(false)}
                            className="p-button-text"
                        />
                        <Button
                            label={t('globals.actions.yes')}
                            icon="pi pi-check"
                            onClick={revokeSession}
                            autoFocus
                        />
                    </div>
                }
            >
                <p>{t('components.session_management_panel.confirm_revoke_message')}</p>
                <p><strong>{parseUserAgent(sessionToRevoke.userAgent)}</strong></p>
                <p>{sessionToRevoke.ipAddress || '-'}</p>
            </Dialog>
        );
    };

    if (!isOwnProfile) return null;

    return (
        <Fieldset className={'SessionManagementPanel'} legend={t("components.session_management_panel.header")}>
            {revokeConfirmDialog()}

            <div className={'col-12 w-full'}>
                <p>{t('components.session_management_panel.note')}</p>
            </div>

            <DataTable
                value={sessions}
                rowHover={true}
                scrollable scrollHeight={'flex'}
                paginator rows={50} rowsPerPageOptions={[50, 100]}
                footer={footerActions()}
            >
                <Column
                    header={t('components.session_management_panel.label_browser')}
                    body={cellParserBrowser}
                    sortable={true}
                    sortField={'userAgent'}
                />
                <Column
                    field={'ipAddress'}
                    header={t('components.session_management_panel.label_ip')}
                    sortable={true}
                    sortField={'ipAddress'}
                    body={(rowData: SessionInfo) => rowData.ipAddress || '-'}
                />
                <Column
                    field={'createdAt'}
                    header={t('components.session_management_panel.label_createdAt')}
                    sortable={true}
                    body={(rowData: SessionInfo) => DateTime.fromISO(rowData.createdAt).toLocaleString(DateTime.DATETIME_SHORT)}
                    sortField={'createdAt'}
                />
                <Column
                    header={t('user_management.actions')}
                    body={cellParserActions}
                    style={{ width: '5rem' }}
                />
            </DataTable>
        </Fieldset>
    );
}

export default SessionManagementPanel;
